
import api from "@/services/api.service";
import { DateTime, Settings } from "luxon";
import { Getter, namespace } from "vuex-class";
import { AreaNote } from "@/store/area-notes/types";
import { Component, Mixins } from "vue-property-decorator";
import GenericTable, { TableHeader } from "../table/generic-table.vue";
import AreaNotesTableItem from "@/components/area-notes/AreaNotesTableItem.vue";
import PaginatedSearch, { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { UserPermissions, AreaNotesPaginatedQueryParams } from "@/store/types";
import AreaNotesPagedResponse from "@/types/sv-data/events/AreaNotesPagedResponse";
import { GenericTableConstants } from '@/types/Constants';

const AreaNotes = namespace("areaNotes");

Settings.defaultLocale = "en";

@Component({
	components: {
		"area-notes-table-item": AreaNotesTableItem,
		"generic-table": GenericTable,
	},
})
export default class AreaNotesTable extends Mixins(PaginatedSearch) {
	@AreaNotes.Getter getCurrentGroupId: number;
	@AreaNotes.Getter getIsFormShown: number;
	@AreaNotes.Action startEditingNote: any;
	@AreaNotes.Mutation setAreaNotes: (data: AreaNote[]) => void;
	@AreaNotes.Mutation setAreaNotesCount: (count: number) => void;

	@AreaNotes.Action public deleteAreaNote: (noteId: number) => Promise<void>;

	@Getter getPermissions: UserPermissions;

	private isLoading: boolean = true;
	private areaNotes: AreaNote[] = [];
	private totalRecords: number = 0;

	private defaultSortDirection: string = GenericTableConstants.SortDirectionDescending;

	// For delete confirmation
	private deletingNote: AreaNote | null = null;

	private columns: TableHeader[] = [
		{
			title: "Created",
			key: "created",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The time the note was created",
			searchable: true,
			visible: true,
			dataType: "date",
			isTermLabel: true,
			sortable: true,
			useCustomCell: true,
			isSortedByDefault: true,
		},
		{
			title: "Note",
			key: "entry",
			order: 2,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The note entry",
			searchable: true,
			visible: true,
			dataType: "title",
			isTermLabel: true,
			sortable: true,
		},
		{
			title: "Starts",
			key: "startFrom",
			order: 3,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The start date and time of the note",
			searchable: true,
			visible: true,
			dataType: "date",
			isTermLabel: true,
			sortable: true,
			useCustomCell: true,
		},
		{
			title: "Ends",
			key: "endAt",
			order: 4,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The end date and time of the note",
			searchable: true,
			visible: true,
			dataType: "date",
			isTermLabel: true,
			sortable: true,
			useCustomCell: true,
		},
		{
			title: "Created By",
			key: "userName",
			order: 5,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The user who created the note",
			searchable: true,
			visible: true,
			dataType: "string",
			isTermLabel: true,
			sortable: true,
		},
	];

	public async mounted(): Promise<void> {
		await this.searchAreaNotes();
	}

	private async searchAreaNotes(paginatedSearchQueryParams: AreaNotesPaginatedQueryParams = null): Promise<void> {
		this.isLoading = true;

		const page: number = 1;
		const pageSize: number = 25;

		if (!paginatedSearchQueryParams) {
			paginatedSearchQueryParams = {
				page: page,
				pageSize: pageSize,
				sortDesc: true,
				sortBy: "Created",
				searchTerm: "",
			} as AreaNotesPaginatedQueryParams;
		}

		const paginatedSearchRequest: PaginatedSearchRequest =
			this.generateNewPaginatedSearchRequest(paginatedSearchQueryParams);

		paginatedSearchQueryParams.groupId = this.getCurrentGroupId;
		const payload = { ...paginatedSearchQueryParams, ...paginatedSearchRequest } as AreaNotesPaginatedQueryParams;

		try {
			const result: AreaNotesPagedResponse = await api.loadAreaNotesForGroup(payload);
			this.areaNotes = result.data as AreaNote[];

			this.setAreaNotes(this.areaNotes);
			this.setAreaNotesCount(result.totalRecords);
			this.totalRecords = result.totalRecords as number;
		} catch (error) {
			console.error("Error fetching area notes: ", error);
		}

		this.isLoading = false;
	}

	private async onModalOpen(note: AreaNote): Promise<void> {
		this.startEditingNote(note);
	}

	private startDeleteAreaNote(note: AreaNote): void {
		this.deletingNote = note;
	}

	private async deleteNote(note: AreaNote): Promise<void> {
		await this.deleteAreaNote(note.noteId);
		this.deletingNote = null;
		this.searchAreaNotes();
	}

	private hasPermissionsToEditNotes(): boolean {
		return this.getPermissions.canViewSiteSetup;
	}

	private displayDateFromString(date: string): string {
		if (!date) {
			return "";
		}
		const utcOffset = "Z";
		const dateTimeFormat = "MM/dd/yyyy hh:mm:ss a";

		if (typeof date === "string") {
			// for areas in UTC timezone there is a 'Z' at the end of the DateTime string.
			// remove the offset from the string.
			if (date.endsWith(utcOffset)) {
				date = date.replace(utcOffset, "");
			}
		}

		return DateTime.fromJSDate(new Date(date)).toFormat(dateTimeFormat);
	}
}
